import React, { memo, useState } from 'react';
import { Grid, Box, useTheme } from '@mui/material';

import Acordeon from './Acordeon/Acordeon';
import FlightSummary from './FlightSummary';
import FlightTitle from './FlightTitle';
import ClusterOptionsList from './ClusterOptionsList';

import { sxStyled } from './styles';
import { getCheapestByPrice, buildClusterBookingBody } from '../helpers';
import { useClusterSelection } from './useClusterSelection';
import { useOptionsPagination } from './useOptionsPagination';

const ClusterPersonalized = memo(
  ({
    element,
    infoFlightsSearch,
    flightsInUSD,
    canBuy,
    canBook,
    CanSaleInUsd,
    handleOpenDrawer,
    screenSize,
    isCustomSearch,
    validateBaggage,
    businessTypes,
    passengers,
    index: clusterIndex,
  }) => {
    const theme = useTheme();
    const sxStyle = sxStyled(theme);

    const {
      activeRadio,
      activeCluster,
      nextLegOptions,
      handleRadio,
      clusterId,
      refNextLeg,
    } = useClusterSelection(element);

    const [seeShareItineraryModal, setSeeShareItineraryModal] = useState(false);

    const setOpenSeeShareItineraryModal = () => {
      setSeeShareItineraryModal(true);
    };

    const { body, legsSelectUnit } = buildClusterBookingBody(
      element,
      activeRadio,
      activeCluster
    );

    const selectedIndex = element.combinations.findIndex(
      c => c.option?.hash === activeRadio[0]?.hash
    );

    const combinationsPagination = useOptionsPagination({
      list: element.combinations,
      screenSize,
      selectedIndex,
      getKey: c => c.option?.hash,
    });

    const selectedIndexNext = nextLegOptions.findIndex(
      n => n.option?.id_option === activeRadio[1]?.id_option
    );

    const nextLegPagination = useOptionsPagination({
      list: nextLegOptions,
      screenSize,
      selectedIndex: selectedIndexNext,
      getKey: n => n.option?.id_option,
    });

    // Cada leg (ida / vuelta) se identifica por indexLeg y empareja un título
    // (FlightTitle) con la columna de opciones (ClusterOptionsList) de abajo.
    // El orden en el DOM difiere entre mobile (título-columna intercalados) y
    // desktop (los dos títulos primero, luego las dos columnas), por eso los
    // títulos y las columnas no se renderizan uno al lado del otro en el JSX.
    const hasNextLeg = Boolean(element.combinations[0].next_leg_options);
    const commonTitle = element.common.common_title;

    const legLabel = indexLeg => (indexLeg === 0 ? 'ida' : 'vuelta');
    const legIdPrefix = indexLeg =>
      `cluster${clusterIndex + 1}-${legLabel(indexLeg)}`;

    const legs = [
      {
        indexLeg: 0,
        idPrefix: legIdPrefix(0),
        pagination: combinationsPagination,
        totalCount: element.combinations.length,
        renderItem: (combination, index) => (
          <Acordeon
            key={`combination-${combination.option.hash}-${index}`}
            idPrefix={`${legIdPrefix(0)}-opcion${index + 1}`}
            element={element}
            index={index}
            object_option={combination.option}
            next_leg_options={combination.next_leg_options}
            next_cluster={
              getCheapestByPrice(combination.next_leg_options)?.cluster ??
              combination.cluster
            }
            title=""
            indexLeg={0}
            activeRadio={activeRadio[0]?.hash === combination.option.hash}
            isForcedSelected={
              combination?.isForcedSelected && !combinationsPagination.isAll
            }
            handleRadio={handleRadio}
            isCustomSearch={isCustomSearch}
          />
        ),
      },
      hasNextLeg && {
        indexLeg: 1,
        idPrefix: legIdPrefix(1),
        pagination: nextLegPagination,
        totalCount: nextLegOptions.length,
        boxRef: refNextLeg,
        renderItem: (nextLeg, index) => (
          <Acordeon
            key={index + nextLeg.option.id_option}
            idPrefix={`${legIdPrefix(1)}-opcion${index + 1}`}
            element={element}
            index={index}
            object_option={nextLeg.option}
            next_cluster={nextLeg?.cluster}
            title=""
            indexLeg={1}
            activeRadio={activeRadio[1]?.id_option === nextLeg.option.id_option}
            isForcedSelected={
              nextLeg?.isForcedSelected && !nextLegPagination.isAll
            }
            handleRadio={handleRadio}
            isCustomSearch={isCustomSearch}
          />
        ),
      },
    ].filter(Boolean);

    return (
      <Grid
        container
        sx={{
          borderRadius: '10px',
          width: 'auto',
          height: 'auto',
          display: 'flex',
          flexDirection: 'column',
          bgcolor: '#fff',
          mb: { xs: '20px', lg: '30px' },
          mx: { xs: '20px', lg: 0 },
          border: { xs: '2px solid #E5E5E5', sm: 'none' },
        }}
        id={`custom-cluster-${clusterId}`}
      >
        <Box sx={sxStyle.cardContent}>
          <Grid
            container
            style={{
              width: '100%',
              height: '100%',
              display: 'flex',
              justifyContent: 'space-evenly',
            }}
          >
            <Grid item xs={12} md={8} style={{ height: 'auto' }}>
              <Grid
                container
                sx={sxStyle.centerItems}
                style={{ marginTop: '4vh' }}
              >
                {screenSize.width > 600 &&
                  commonTitle.map((elementLeg, indexLeg) => (
                    <React.Fragment key={indexLeg + elementLeg?.title}>
                      <FlightTitle
                        id={legIdPrefix(indexLeg) + '-titulo'}
                        element={element}
                        indexLeg={indexLeg}
                        elementLeg={elementLeg}
                      />
                    </React.Fragment>
                  ))}

                {screenSize.width < 600 && (
                  <FlightTitle
                    id={legIdPrefix(0) + '-titulo'}
                    element={element}
                    indexLeg={0}
                    elementLeg={commonTitle[0]}
                    isMobile={true}
                  />
                )}

                <Grid
                  item
                  sm={hasNextLeg ? 6 : 12}
                  xs={12}
                  sx={sxStyle.gridClusterItem}
                >
                  <ClusterOptionsList
                    idPrefix={legs[0].idPrefix}
                    items={legs[0].pagination.visible}
                    totalCount={legs[0].totalCount}
                    isAllVisible={legs[0].pagination.isAll}
                    onShowAll={legs[0].pagination.showAll}
                    sxStyle={sxStyle}
                  >
                    {legs[0].renderItem}
                  </ClusterOptionsList>
                </Grid>

                {screenSize.width < 600 && (
                  <FlightTitle
                    id={legIdPrefix(1) + '-titulo'}
                    element={element}
                    indexLeg={1}
                    elementLeg={commonTitle[1]}
                    isMobile={true}
                  />
                )}

                {hasNextLeg && (
                  <Grid item sm={6} xs={12} sx={sxStyle.gridClusterItem}>
                    <ClusterOptionsList
                      idPrefix={legs[1].idPrefix}
                      items={legs[1].pagination.visible}
                      totalCount={legs[1].totalCount}
                      isAllVisible={legs[1].pagination.isAll}
                      onShowAll={legs[1].pagination.showAll}
                      sxStyle={sxStyle}
                      boxRef={legs[1].boxRef}
                    >
                      {legs[1].renderItem}
                    </ClusterOptionsList>
                  </Grid>
                )}
              </Grid>
            </Grid>

            <Grid sx={sxStyle.hideOnMobile}>
              <Box sx={sxStyle.separatorVertical}></Box>
            </Grid>

            <Grid item xs={10} md={3} sx={{ height: 'auto' }}>
              <FlightSummary
                element={element}
                flightsInUSD={flightsInUSD}
                infoFlightsSearch={infoFlightsSearch}
                activeRadio={activeRadio}
                common_title={commonTitle}
                passengers={passengers}
                canBuy={canBuy}
                canBook={canBook}
                body={body}
                legsSelectUnit={legsSelectUnit}
                businessTypes={businessTypes}
                setOpenSeeShareItineraryModal={setOpenSeeShareItineraryModal}
                seeShareItineraryModal={seeShareItineraryModal}
                setSeeShareItineraryModal={setSeeShareItineraryModal}
                handleOpenDrawer={handleOpenDrawer}
                CanSaleInUsd={CanSaleInUsd}
                validateBaggage={validateBaggage}
                isCustomSearch={isCustomSearch}
                activeCluster={activeCluster}
              />
            </Grid>
          </Grid>
        </Box>
      </Grid>
    );
  }
);

export default ClusterPersonalized;
